import React from 'react'
import {
  toDollars,
  useLineItemsExludingTotals
} from '../../../utils/cart-helpers'
import { Cart } from '../../../types/cart'
import { useCurrency } from '@local/do-secundo-use-currency'
import { CheckoutPaymentAmount } from '../../CheckoutForm/useCheckoutProps'
import { getTotalAmountPaid, getAmountDue } from '../../../utils/payment-util'
import { t } from '@local/translations'

interface Props {
  cart: Cart
  showTotal: boolean
  showTip: boolean
  prePaymentTip?: number
  checkoutPaymentAmount?: CheckoutPaymentAmount
  showRemainingBalance: boolean
}

export const LineItems: React.FC<Props> = ({
  cart,
  showTotal,
  showTip,
  prePaymentTip,
  checkoutPaymentAmount,
  showRemainingBalance
}) => {
  const { currencyFormatter } = useCurrency()

  const getLineItems = useLineItemsExludingTotals(cart)
  const lineItems = getLineItems()

  const check = cart.order.checks[0]!!
  const depositRequired = checkoutPaymentAmount?.isPartialPaymentRequired

  const totalAmountDue = depositRequired
    ? checkoutPaymentAmount.totalAmountDue
    : check.totalAmount

  const displayTip = prePaymentTip || check.tipAmount || 0

  // After the order has been placed, the check's total amount accounts
  // for tip. So we only need to add it if it's coming from the prePaymentTip.
  const totalAmountDueWithTip = totalAmountDue + (prePaymentTip ?? 0)

  // Used on the confirmation page
  const totalAmountPaid = getTotalAmountPaid(check)
  const isPartiallyPaid =
    totalAmountPaid > 0 && totalAmountPaid !== check.totalAmount

  const renderTotalOrderAmount = () =>
    showTotal && (
      <LineItemRow
        label={t('line-items.total')}
        amount={toDollars(check.totalAmount)}
        id={'total-order-amount'}
      />
    )

  const renderDepositAmount = ({ bold }: { bold: boolean }) =>
    depositRequired && (
      <LineItemRow
        label={t('line-items.deposit-percentage', {
          percentage: checkoutPaymentAmount.partialPaymentPercentage
        })}
        amount={toDollars(checkoutPaymentAmount.totalAmountDue)}
        id={'total-deposit-amount'}
        className={bold ? 'font-semibold' : undefined}
      />
    )

  const renderTipAmount = () =>
    showTip && (
      <LineItemRow
        label={t('line-items.tip')}
        amount={toDollars(displayTip)}
        id={'tip-amount'}
      />
    )

  const renderTotalAmount = () => (
    <LineItemRow
      label={t(
        depositRequired ? 'line-items.total-due-today' : 'line-items.total'
      )}
      amount={toDollars(totalAmountDueWithTip)}
      id={'total-amount-due'}
      className={'font-semibold'}
    />
  )

  const renderPaymentAmount = () =>
    totalAmountPaid && (
      <LineItemRow
        label={t('line-items.total-paid')}
        amount={toDollars(totalAmountPaid)}
        id={'total-paid'}
        className={'font-semibold'}
      />
    )

  const renderRemainingBalance = () => {
    if (!showRemainingBalance) return undefined
    const remainingBalance = getAmountDue(
      check,
      checkoutPaymentAmount?.totalAmountDue
    )
    return (
      <tr>
        <td colSpan={2} className='text-left'>
          {t('line-items.remaining-balance', {
            balance: currencyFormatter(remainingBalance)
          })}
        </td>
      </tr>
    )
  }

  return (
    <>
      {lineItems.map((lineItem) => (
        <LineItemRow key={lineItem.id} {...lineItem} />
      ))}
      {depositRequired ? (
        <>
          {renderTotalOrderAmount()}
          <LineItemRowBreak />
          {renderDepositAmount({ bold: !showTip })}
          {renderTipAmount()}
          {showTip && renderTotalAmount()}
          {renderRemainingBalance()}
        </>
      ) : (
        <>
          {renderTipAmount()}
          {renderTotalAmount()}
          {isPartiallyPaid && (
            <>
              <LineItemRowBreak />
              {renderPaymentAmount()}
              {renderRemainingBalance()}
            </>
          )}
        </>
      )}
    </>
  )
}

const LineItemRow = (props: {
  label: string
  amount: number
  id: string
  className?: string
}) => {
  const { label, amount, id, className } = props
  const { currencyFormatter } = useCurrency()

  return (
    <tr data-testid={id} className={className}>
      <td className={'flex items-center'}>{label}</td>
      <td>
        {currencyFormatter(amount, {
          renderWhenZero:
            id === 'deliveryChargeTotal-amount' ? 'Free' : undefined
        })}
      </td>
    </tr>
  )
}

const LineItemRowBreak = () => (
  <tr className='pointer-events-none'>
    <td colSpan={2}>
      <hr className='bg-darken-12 h-px border-0 p-0 mx-0 my-4' />
    </td>
  </tr>
)
